import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { extractApiError } from "../lib/errors";
import { toast } from "sonner";
import { motion } from "framer-motion";

const LOGO = "/ashra-logo.png";

export default function Signup() {
  const nav = useNavigate();
  const { signup } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/onboarding";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(name, email, password);
      toast.success("Your AI revenue team is ready");
      nav("/onboarding");
    } catch (err) {
      toast.error(extractApiError(err, "Signup failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50 text-zinc-900">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-30 ashra-gradient-bg" />
      </div>
      <div className="relative min-h-screen grid place-items-center p-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md rounded-3xl bg-white border border-black/5 shadow-2xl shadow-violet-500/10 p-8">
          <Link to="/" className="flex items-center justify-center" data-testid="signup-logo">
            <img src={LOGO} alt="Ashra AI" className="h-12 sm:h-14 w-auto" />
          </Link>
          <h1 className="mt-6 font-display text-3xl font-bold text-center">Start your AI revenue team</h1>
          <p className="mt-1 text-sm text-zinc-500 text-center">Free plan. No credit card. 2-minute setup.</p>

          <button type="button" onClick={googleLogin} data-testid="google-signup-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 px-5 py-3 text-sm font-medium">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.2-2 13.8-5.3l-6.4-5.4C29.5 34.9 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.4 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.3l6.4 5.4C41.9 34.8 44 29.8 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-zinc-400">
            <span className="flex-1 h-px bg-zinc-200" /> or <span className="flex-1 h-px bg-zinc-200" />
          </div>
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-violet-500" data-testid="signup-name" />
            <input required type="email" placeholder="you@creator.com" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-violet-500" data-testid="signup-email" />
            <input required type="password" minLength={6} placeholder="Create a password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-violet-500" data-testid="signup-password" />
            <button type="submit" disabled={loading}
              className="w-full rounded-xl ashra-gradient-bg text-white font-semibold py-3 text-sm hover:opacity-95 transition disabled:opacity-60" data-testid="signup-submit">
              {loading ? "Creating…" : "Create workspace"}
            </button>
          </form>
          <p className="mt-6 text-sm text-zinc-500 text-center">
            Have an account? <Link to="/login" className="text-violet-600 font-medium" data-testid="go-login">Log in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
